import { Http } from '@/services/http'
import { ParamsClean } from '@/common/decorators/params-clean'
import { Injectable } from '@tanbo/vue-di-plugin'
import { ProtectionType, Level, Status } from './types/ransomware-incident'
export interface RequestParams {
  /** 分组id */
  group_id: number
  /** 防护类型  1-诱饵文件篡改 2-其他文件篡改 3-主导记录（MBR）篡改 4-卷影删除 5-暴力破解 6-漏洞利用 7-勒索特征识别 */
  defence_type: number | ProtectionType
  /** 页码 */
  page: number
  /** 每页数量 */
  limit: number
  /** 结束时间 */
  end_time?: number
  /** 搜索关键词 */
  key_word?: string
  /** 等级 */
  level?: number | Level
  /** 开始时间 */
  start_time?: number
  // 日期
  date?: (string|number)[]
  // 设备Id
  device_id: string
  /**
   * 处理状态多选（API）：0-已阻止 1-已允许 2-阻止并结束 3-仅记录。
   * 统计卡「已阻止事件」传 [0, 2]；下拉筛选手动仅 0/1/3 映射为单元素数组。
   */
  statuses?: number[]
}

export interface Statistics {
  /** 类型:1-诱饵文件篡改 2-其他文件篡改 3-主导记录（MBR）篡改 4-卷影删除 5-暴力破解 6-漏洞利用 7-勒索特征识别 */
  defence_type?: number | ProtectionType
  /** 数量 */
  count?: number
}

export interface RansomwareProtectRow {
  /** 计算机名 */
  computer_name: string
  /** 内网ip,以"，"分隔 */
  system_ip: string
  /** 防护状态 1-开启，2-关闭 */
  defence_status: number
  /** 登记用户名 */
  user_mark: string
  /** Mac地址 */
  mac: string
  /** 外网ip */
  client_ip: string
  /** 分组名 */
  group_name: string
  /** 终端设备id */
  device_id: string
  /** 事件描述 */
  described?: string
  /** 等级 */
  level?: number | Level
  /** 操作时间 */
  operate_time?: string
  /** 处理状态 */
  status?: number | Status
  /** 威胁类型 */
  tags?: string[],
  /** 勒索病毒状态 */
  defence_type: number | ProtectionType
  /** 详情展示字段-源	*/
  operator_fields: object
  /** 详情展示字段-目标 */
  target_fields: object
  // 病毒信息
  risk_info: string
}

export interface ResponseData {
  /** 事件列表数据 */
  list?: RansomwareProtectRow[]
  /** 分类统计 */
  statistics?: Statistics[]
}


// 注意：DefenceType 和 RiskLevel 已迁移到 types/ransomware-incident.ts
// 请使用 ProtectionType 和 Level 枚举
// DefenceType 保留用于向后兼容，但建议使用 ProtectionType
export enum DefenceType {
  DecoyFileTampering = 1, // 诱饵文件篡改
  OtherFileTampering = 2, // 其他文件篡改
  MbrTampering = 3,       // 主导记录 (MBR) 篡改
  ShadowCopyDeletion = 4, // 卷影删除
  BruteForce = 5,         // 暴力破解
  Exploit = 6,            // 漏洞利用
  RansomwareSignature = 7 // 勒索特征识别
}

// 风险等级枚举 (对应 level)
// 注意：RiskLevel 已迁移到 types/ransomware-incident.ts 的 Level 枚举
// 请使用 Level 枚举，RiskLevel 保留用于向后兼容
export enum RiskLevel {
  Unknown = 0, // 未知
  Low = 1,     // 低危
  Medium = 2,  // 中危
  High = 3     // 高危
}

// 标签对象 (对应 tags 数组中的元素)
export interface ThreatTag {
  name?: string;
  type?: string;
}

// 统计信息对象 (对应 statistics)
export interface StatisticsItem {
  defence_type?: DefenceType | ProtectionType;
  count?: number;
}

// 列表项对象 (对应 list 数组中的元素)
export interface EventListItem {
  operate_time?: string; // 格式: int64 (时间戳字符串)
  level?: RiskLevel | Level;
  tags?: ThreatTag[];
  defence_type?: DefenceType | ProtectionType;
  status?: number | Status; // 处理状态
  computer_name: string; // 必须: 计算机名
  system_ip: string;     // 必须: 内网ip,以","分隔
  user_mark: string;     // 必须: 登记用户名
  mac: string;           // 必须: Mac地址
  client_ip: string;     // 必须: 外网ip
  group_name: string;    // 必须: 分组名
  device_id: string;     // 必须: 终端设备id

  // 详情展示字段 (通常是一个动态对象)
  operator_fields?: Record<string, any>; // 源
  target_fields?: Record<string, any>;   // 目标

  system_name: string;       // 必须: 系统名称
  operator_scan_result?: number;
  system_type?: number;
  risk_info?: string;        // 病毒信息
  total?: number;
  blocked_count?: number;    // 已阻止事件数
}

// 根接口定义
export interface SecurityEventResponse {
  statistics?: StatisticsItem[]; // 图片显示类型为 object []
  list?: EventListItem[];
  total?:number
  blocked_count?:number
}

export interface RequestParamsReport {
  /** 分组id */
  group_id: number
  /** 页码 */
  page: number
  /** 每页数量 */
  limit: number
  /** 搜索关键词 */
  key_word?: string
}

export interface ResponseDataReportRow {
  /** 终端设备id */
  device_id: string
  /** 计算机名 */
  computer_name: string
  /** 内网ip,以"，"分隔 */
  system_ip: string
  /** 防护状态 1-开启，2-关闭 */
  defence_status: number
  /** 登记用户名 */
  user_mark: string
  /** Mac地址 */
  mac: string
  /** 终端登录状态,1-在线，2-离线 */
  login_status: number
  /** 外网ip */
  client_ip: string
  /** 分组名 */
  group_name: string
  /** 系统类型,0-全部，1-windows,2-linux，3-windows server，4-mac */
  system_type: number
  // 系统名称
  system_name: string
  /** 勒索防护事件数量 */
  event_count?: number
}

export interface ResponseDataReport {
  list?: ResponseDataReportRow[]
  total?: number
}
export interface statsRequestParams {
  /** 结束时间 */
  end_time?: string
  /** 开始时间 */
  start_time?: string
}

export interface statsResponseDataItem {
  /** 类型:1-诱饵文件篡改 2-其他文件篡改 3-主导记录（MBR）篡改 4-卷影删除 5-暴力破解 6-漏洞利用 7-勒索特征识别 */
  defence_type: number
  /** 数量 */
  count: number
}

export interface statsResponseData {
  statistics?: statsResponseDataItem[]
}
@Injectable()
export class RansomwareService {
  constructor (private http: Http) {
  }
  // 勒索防护事件列表
  @ParamsClean()
  getRansomwareProtectList (data: RequestParams) {
    const dataCopy = { ...data } as Record<string, unknown>
    delete dataCopy.date
    dataCopy.level = !data.level ? -1 : data.level
    dataCopy.status ||= -1
    delete dataCopy.status_list
    delete dataCopy.status_filter
    if (!Array.isArray(dataCopy.statuses) || dataCopy.statuses.length === 0) {
      delete dataCopy.statuses
    }
    for (const item of Object.keys(dataCopy)) {
      const v = dataCopy[item]
      if (v === undefined || v === '') {
        delete dataCopy[item]
      }
    }
    return this.http.post<SecurityEventResponse>('/ransomwareprotect/list', dataCopy)
  }
  @ParamsClean([0], 1)
  getRansomwareProtectExport (type: 0|1 = 0, options: Partial<RequestParams>, moduleName: string) {
    const dataCopy = { ...options } as Record<string, unknown>
    delete dataCopy.date
    dataCopy.level = !options.level ? -1 : options.level
    delete dataCopy.status
    delete dataCopy.status_list
    delete dataCopy.status_filter
    if (!Array.isArray(dataCopy.statuses) || dataCopy.statuses.length === 0) {
      delete dataCopy.statuses
    }
    for (const item of Object.keys(dataCopy)) {
      const v = dataCopy[item]
      if (v === undefined || v === '') {
        delete dataCopy[item]
      }
    }
    const params = Object.assign({}, dataCopy, {
      file_type: type,
      page: 1, // 不分页
      limit: 9999999
    })
    return this.http.downloadWithBlob('/ransomwareprotect/export', params, moduleName)
  }
  // 勒索防护终端统计列表
  @ParamsClean()
  getRansomwareProtectReport (data: RequestParamsReport) {
    return this.http.post<ResponseDataReport>('/ransomwareprotect/report', data)
  }

 getRansomwareprotectStats (data: statsRequestParams) {
    return this.http.post<statsResponseData>('/ransomwareprotect/stats', data)
  }
}
